import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Filter, Eye, Mail, Phone, Calendar, Pencil, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import LeadEditModal from '@/components/admin/LeadEditModal';
import {
  getLeadStatusColor,
  formatLeadStatusLabel,
  getLeadTypeLabel,
  LEAD_CONVERTED_EDIT_DENIED_MESSAGE,
} from '@/components/admin/LeadDetailsContent';
import DateSectionHeader from '@/components/admin/DateSectionHeader';
import { groupItemsByDateSection } from '@/utils/dateSections';
import { formatDateTimeMdY } from '@/utils/dateFormat';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/utils/vehiclePrice';
import AdminPageHeader from '@/components/admin/AdminPageHeader';
import { AdminKpiTile } from '@/components/admin/AdminKpiGrid';
import AdminFilterBar from '@/components/admin/AdminFilterBar';
import AdminRecordCard from '@/components/admin/AdminRecordCard';
import AdminOverflowMenu from '@/components/admin/AdminOverflowMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TrackLeads() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [editLead, setEditLead] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads(response.data);
      setFilteredLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchLeads();
    }
  }, [isAuthenticated, token, fetchLeads]);

  // Filter leads
  useEffect(() => {
    let filtered = leads;

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.type === typeFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (lead) =>
          (lead.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (lead.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (lead.phone || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLeads(filtered);
    setCurrentPage(1);
  }, [searchTerm, typeFilter, leads]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
  const pagedLeads = useMemo(
    () => filteredLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filteredLeads, currentPage, pageSize],
  );
  const groupedLeads = useMemo(
    () => groupItemsByDateSection(pagedLeads, (lead) => lead.created_at),
    [pagedLeads],
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const getTypeBadgeColor = (type) => {
    const colors = {
      contact: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
      vehicle_inquiry: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
      financing: 'bg-green-500/10 text-green-400 border-green-500/30',
      trade_in: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
      prequalification: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
      chatbot: 'bg-pink-500/10 text-pink-400 border-pink-500/30',
    };
    return colors[type] || colors.contact;
  };

  const getStatusBadge = (lead) => (
    <span className={`px-3 py-1 text-xs font-['Manrope'] border ${getLeadStatusColor(lead.status)}`}>
      {formatLeadStatusLabel(lead.status)}
      {lead.is_converted && (
        <span className="ml-1.5 text-[10px] opacity-70">· Synced</span>
      )}
    </span>
  );

  const formatDate = (dateString) => formatDateTimeMdY(dateString);

  const handleViewDetails = (lead) => {
    navigate(`/admin/leads/${lead.id}`);
  };

  const handleEditLead = (lead) => {
    if (lead.is_converted) {
      toast.error(LEAD_CONVERTED_EDIT_DENIED_MESSAGE, {
        action: lead.deal_id ? {
          label: 'Open Deal',
          onClick: () => navigate(`/admin/deals/${lead.deal_id}`),
        } : undefined,
      });
      return;
    }
    setEditLead(lead);
    setIsEditModalOpen(true);
  };

  const handleLeadSaved = (updatedLead) => {
    setLeads((prev) => prev.map((l) => (l.id === updatedLead.id ? { ...l, ...updatedLead } : l)));
  };

  const handleLeadDeleted = (deletedLead) => {
    setLeads((prev) => prev.filter((l) => l.id !== deletedLead.id));
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="track-leads-page">
      <AdminPageHeader
        title="Track Leads"
        subtitle="Monitor and manage incoming leads"
      />

      <div>
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3 mb-8">
          {[
            { key: 'all', label: 'Total Leads', color: 'text-white' },
            { key: 'contact', label: getLeadTypeLabel('contact'), color: 'text-blue-400' },
            { key: 'vehicle_inquiry', label: getLeadTypeLabel('vehicle_inquiry'), color: 'text-purple-400' },
            { key: 'financing', label: getLeadTypeLabel('financing'), color: 'text-green-400' },
            { key: 'trade_in', label: getLeadTypeLabel('trade_in'), color: 'text-orange-400' },
            { key: 'prequalification', label: getLeadTypeLabel('prequalification'), color: 'text-cyan-400' },
            { key: 'chatbot', label: getLeadTypeLabel('chatbot'), color: 'text-pink-400' },
          ].map((tile) => (
            <AdminKpiTile
              key={tile.key}
              label={tile.label}
              value={tile.key === 'all' ? leads.length : leads.filter((lead) => lead.type === tile.key).length}
              color={tile.color}
              active={tile.key === 'all' ? typeFilter === 'all' : typeFilter === tile.key}
              testId={`stat-leads-${tile.key}`}
              onClick={() => {
                setCurrentPage(1);
                if (tile.key === 'all') {
                  setTypeFilter('all');
                  return;
                }
                setTypeFilter((current) => (current === tile.key ? 'all' : tile.key));
              }}
            />
          ))}
        </div>

        <AdminFilterBar
          search={searchTerm}
          onSearch={setSearchTerm}
          searchPlaceholder="Search by name, email, or phone..."
        >
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 pointer-events-none" />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full lg:w-auto bg-white/5 border border-white/10 text-white pl-11 pr-8 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
            >
              <option value="all">All Types</option>
              <option value="contact">{getLeadTypeLabel('contact')}</option>
              <option value="vehicle_inquiry">{getLeadTypeLabel('vehicle_inquiry')}</option>
              <option value="financing">{getLeadTypeLabel('financing')}</option>
              <option value="trade_in">{getLeadTypeLabel('trade_in')}</option>
              <option value="prequalification">{getLeadTypeLabel('prequalification')}</option>
              <option value="chatbot">{getLeadTypeLabel('chatbot')}</option>
            </select>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="w-full lg:w-auto bg-white/5 border border-white/10 text-white px-4 py-3 font-['Manrope'] focus:outline-none focus:border-blue-500/50 appearance-none cursor-pointer [&>option]:bg-gray-900 [&>option]:text-white"
            data-testid="leads-page-size-select"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} per page
              </option>
            ))}
          </select>
        </AdminFilterBar>

        {/* Leads Table */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-white/60 font-['Manrope']">
              Loading leads...
            </div>
          ) : filteredLeads.length === 0 ? (
            <div className="p-12 text-center text-white/60 font-['Manrope']">
              {leads.length === 0 ? (
                <p>No leads yet.</p>
              ) : (
                <>
                  <p>No leads match your filters.</p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setTypeFilter('all');
                      setCurrentPage(1);
                    }}
                    className="mt-4 min-h-11 px-4 border border-white/15 text-white text-sm font-['Manrope'] hover:bg-white/5"
                  >
                    Clear filters
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
            <div className="lg:hidden space-y-3 p-3">
              {groupedLeads.map((section) => (
                <React.Fragment key={section.key}>
                  <DateSectionHeader variant="list" label={section.label} count={section.items.length} />
                  {section.items.map((lead) => (
                    <AdminRecordCard
                      key={lead.id}
                      title={lead.name}
                      subtitle={lead.email}
                      badge={
                        <span className={`px-2 py-1 text-[11px] font-['Manrope'] border ${getTypeBadgeColor(lead.type)}`}>
                          {getLeadTypeLabel(lead.type)}
                        </span>
                      }
                      fields={[
                        { label: 'Status', value: formatLeadStatusLabel(lead.status) },
                        { label: 'Phone', value: lead.phone || '—' },
                        { label: 'Date', value: formatDate(lead.created_at) },
                      ]}
                      onClick={() => handleViewDetails(lead)}
                      primary={
                        <button
                          type="button"
                          onClick={() => handleViewDetails(lead)}
                          className="w-full min-h-11 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold"
                        >
                          Open
                        </button>
                      }
                      overflow={
                        <AdminOverflowMenu
                          items={[
                            { label: 'Edit', onClick: () => handleEditLead(lead), disabled: lead.is_converted },
                            lead.deal_id
                              ? { label: 'View deal', onClick: () => navigate(`/admin/deals/${lead.deal_id}`) }
                              : null,
                          ]}
                        />
                      }
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-['Manrope'] font-semibold text-white/80 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {groupedLeads.map((section) => (
                    <React.Fragment key={section.key}>
                      <DateSectionHeader label={section.label} count={section.items.length} colSpan={6} />
                      {section.items.map((lead) => (
                    <tr
                      key={lead.id}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="font-['Manrope'] font-semibold text-white">
                          {lead.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <a
                            href={`mailto:${lead.email}`}
                            className="flex items-center gap-2 text-sm font-['Manrope'] text-blue-400 hover:text-blue-300"
                          >
                            <Mail className="w-3 h-3" />
                            {lead.email}
                          </a>
                          {lead.phone && (
                            <a
                              href={`tel:${lead.phone}`}
                              className="flex items-center gap-2 text-sm font-['Manrope'] text-green-400 hover:text-green-300"
                            >
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={`px-3 py-1 text-xs font-['Manrope'] border ${getTypeBadgeColor(lead.type)}`}>
                            {getLeadTypeLabel(lead.type)}
                          </span>
                          {lead.contact_quality?.verdict === 'flag' && (
                            <span className="px-2 py-0.5 text-[10px] font-['Manrope'] border border-amber-500/40 bg-amber-500/10 text-amber-300">
                              Flagged
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(lead)}
                          {lead.is_converted && lead.deal_id && (
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/deals/${lead.deal_id}`)}
                              className="text-[10px] font-['Manrope'] text-blue-400/80 hover:text-blue-300 flex items-center gap-1 w-fit"
                            >
                              View in Deals Hub <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-white/60 font-['Manrope'] text-sm">
                          <Calendar className="w-4 h-4" />
                          {formatDate(lead.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => handleViewDetails(lead)}
                            className="p-2 hover:bg-white/10 transition-colors"
                            title="View Details"
                            data-testid={`view-lead-${lead.id}`}
                          >
                            <Eye className="w-4 h-4 text-blue-400" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditLead(lead)}
                            className="p-2 hover:bg-white/10 transition-colors"
                            title={lead.is_converted ? 'Converted — edit in Deals Hub' : 'Edit Lead'}
                            data-testid={`edit-lead-${lead.id}`}
                          >
                            <Pencil className={`w-4 h-4 ${lead.is_converted ? 'text-white/25' : 'text-amber-400'}`} />
                          </button>
                        </div>
                      </td>
                    </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          {filteredLeads.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-t border-white/10">
              <p className="text-sm font-['Manrope'] text-white/50">
                Showing {pagedLeads.length} of {filteredLeads.length} · Page {currentPage} of {totalPages}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="min-h-11 px-3 py-1.5 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[2.25rem] px-2 py-1.5 text-sm font-['Manrope'] ${
                        page === currentPage
                          ? 'bg-blue-500 text-white'
                          : 'border border-white/10 text-white/70 hover:bg-white/5'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="min-h-11 px-3 py-1.5 border border-white/10 text-white/70 text-sm font-['Manrope'] disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <LeadEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditLead(null);
        }}
        lead={editLead}
        token={token}
        onSaved={handleLeadSaved}
        onDeleted={handleLeadDeleted}
      />
    </div>
  );
}
